import { useCallback, useState, type ReactNode } from "react";
import { Icon, Segmented } from "@app/ui";
import { useT } from "../i18n.js";

/**
 * Shared plumbing for the record-listing pages (products, passports): the
 * list/card view switch, its persistence, and the card grid the "card" mode
 * renders into. Both pages consume this so the two listings stay identical in
 * behaviour and a user's choice on one doesn't contradict the other.
 */

export type ViewMode = "list" | "grid";

const STORAGE_PREFIX = "dpp.view.";

/**
 * View mode for one listing, persisted per page key. Reading in the initializer
 * (not an effect) means the stored mode is the first thing painted — no flash of
 * the list view before the card view takes over.
 */
export function useViewMode(key: string, fallback: ViewMode = "list"): [ViewMode, (v: ViewMode) => void] {
  const [mode, setMode] = useState<ViewMode>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_PREFIX + key);
      return stored === "grid" || stored === "list" ? stored : fallback;
    } catch {
      return fallback;
    }
  });

  const set = useCallback(
    (v: ViewMode) => {
      setMode(v);
      try {
        localStorage.setItem(STORAGE_PREFIX + key, v);
      } catch {
        /* storage unavailable — keep the choice in memory for this session */
      }
    },
    [key],
  );

  return [mode, set];
}

export function ViewModeSwitch({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  const t = useT();
  return (
    <Segmented<ViewMode>
      label={t("common.view")}
      value={value}
      onChange={onChange}
      options={[
        { value: "list", icon: <Icon name="list" size={15} />, title: t("common.viewList") },
        { value: "grid", icon: <Icon name="grid" size={15} />, title: t("common.viewGrid") },
      ]}
    />
  );
}

/**
 * Toolbar above a listing: filters on the left, count + view switch on the right.
 * Wraps to two rows on narrow screens rather than squeezing the search field.
 */
export function ListToolbar({ filters, right }: { filters: ReactNode; right: ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex min-w-0 flex-wrap items-center gap-2">{filters}</div>
      <div className="flex shrink-0 items-center gap-3">{right}</div>
    </div>
  );
}

export function CardGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">{children}</div>;
}

export interface EntityCardProps {
  onOpen: () => void;
  /** Accessible name for the card's activation control. */
  openLabel: string;
  title: ReactNode;
  /** Secondary identifier under the title (SKU, slug) — rendered monospace. */
  code?: ReactNode;
  status?: ReactNode;
  meta?: { key: string; label: ReactNode; value: ReactNode }[];
  footer?: ReactNode;
}

/** One record in the card view. The whole card is the hit target. */
export function EntityCard({ onOpen, openLabel, title, code, status, meta, footer }: EntityCardProps) {
  return (
    // The whole card is the hit target, so the correct element is a real button
    // (free keyboard activation + focus ring); @app/ui's <Button> is a control
    // chrome, not a surface, and would fight this layout.
    // eslint-disable-next-line no-restricted-syntax
    <button
      type="button"
      onClick={onOpen}
      aria-label={openLabel}
      className="group flex h-full flex-col rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/40 hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-medium text-foreground">{title}</div>
          {code != null && (
            <div className="mt-0.5 truncate font-mono text-xs text-muted-foreground">{code}</div>
          )}
        </div>
        {status != null && <div className="shrink-0">{status}</div>}
      </div>

      {meta != null && meta.length > 0 && (
        // Two aligned columns rather than label-left / value-right: at card width
        // a right-aligned value ends up touching its own label and the values
        // never line up with each other.
        <dl className="mt-4 grid grid-cols-[minmax(0,6.5rem)_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
          {meta.map((m) => (
            <div key={m.key} className="contents">
              <dt className="truncate self-baseline text-xs text-muted-foreground">{m.label}</dt>
              <dd className="truncate self-baseline">{m.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {footer != null && (
        <div className="mt-auto flex items-center justify-between gap-2 border-t border-border/60 pt-3 text-xs text-muted-foreground">
          <span className="truncate">{footer}</span>
          <Icon
            name="chevron-right"
            size={14}
            className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          />
        </div>
      )}
    </button>
  );
}

/** Placeholder cards while a page of records loads — mirrors the table skeleton. */
export function CardGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <CardGrid>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="h-[168px] animate-pulse rounded-xl border border-border bg-muted/40" />
      ))}
    </CardGrid>
  );
}
